import React, { useEffect, useMemo, useRef, useState } from "react";
import { Stack } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import styles from "./Answer.module.css";
import { ChatAppResponse, configApi, getCitationFilePath, SpeechConfig } from "../../api";
import { citationFromHref, parseAnswerToHtml } from "./AnswerParser";
import { ClipboardList, ClipboardCheck, Copy, Lightbulb } from "lucide-react";
import { SpeechOutputBrowser } from "./SpeechOutputBrowser";
import { SpeechOutputAzure } from "./SpeechOutputAzure";
import { erholCitationKind } from "../LiveCitation/liveCitationModel";

interface Props {
    answer: ChatAppResponse;
    index: number;
    speechConfig: SpeechConfig;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    showSpeechOutputBrowser?: boolean;
    showSpeechOutputAzure?: boolean;
}

export const Answer = ({
    answer,
    index,
    speechConfig,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    showSpeechOutputAzure,
    showSpeechOutputBrowser
}: Props) => {
    const followupQuestions = answer.context?.followup_questions;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer, isStreaming), [answer, isStreaming]);
    const { t } = useTranslation();
    const getCitationLabel = (citation: string): string => {
        const erholKind = erholCitationKind(citation);
        return erholKind
            ? t(`liveCitation.sources.${erholKind}`)
            : citation.startsWith("live/") ? `${citation.slice(5).replace(/\.json$/, "").replace(/-/g, " ")} (live)` : citation;
    };
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml, {
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|ftp|tel|data|blob):|[^a-z]|[a-z+.-]+(?:[^a-z+.-]|$))/i,
        ADD_URI_SAFE_ATTR: ["href", "src"]
    });
    const [copied, setCopied] = useState(false);
    const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false);

    const getConfig = async () => {
        configApi().then(config => {
            setShowAdvancedSettings(config.showAdvancedSettings);
        });
    };

    useEffect(() => {
        getConfig();
    }, []);

    const handleCopy = () => {
        // Single replace to remove all HTML tags to remove the citations
        const textToCopy = sanitizedAnswerHtml.replace(/<a [^>]*><sup>\d+<\/sup><\/a>|<[^>]+>/g, "");

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => console.error("Failed to copy text: ", err));
    };

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <div>
                        {showSpeechOutputAzure && (
                            <SpeechOutputAzure answer={sanitizedAnswerHtml} index={index} speechConfig={speechConfig} isStreaming={isStreaming} />
                        )}
                        {showSpeechOutputBrowser && <SpeechOutputBrowser answer={sanitizedAnswerHtml} />}
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div className={styles.answerText}>
                    <ReactMarkdown
                        children={sanitizedAnswerHtml}
                        rehypePlugins={[rehypeRaw]}
                        remarkPlugins={[remarkGfm]}
                    components={{
                        a: ({ node, href, ...props }) => {
                            const isBlobLink = typeof href === "string" && href.startsWith("blob:");
                            const isCitationMarker = typeof props.className === "string"
                                && props.className.split(/\s+/).includes("supContainer");
                            const citation = isCitationMarker ? citationFromHref(href, parsedAnswer.citations) : undefined;
                            const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                if (citation) {
                                    event.preventDefault();
                                    onCitationClicked(getCitationFilePath(citation));
                                    return;
                                }
                                if (!isBlobLink || !href) return;
                                event.preventDefault();
                                const opened = window.open(href, "_blank");
                                if (!opened) {
                                    window.location.href = href;
                                }
                            };

                            return (
                                <a
                                    {...props}
                                    href={href}
                                    className={citation ? styles.supContainer : props.className}
                                    title={citation ? getCitationLabel(citation) : props.title}
                                    aria-label={citation ? `${t("citationWithColon")} ${getCitationLabel(citation)}` : props["aria-label"]}
                                    target={props.target || (isBlobLink ? "_blank" : undefined)}
                                    rel={props.rel || (isBlobLink ? "noreferrer noopener" : undefined)}
                                    download={isBlobLink ? true : undefined}
                                    onClick={citation || isBlobLink ? handleClick : props.onClick}
                                />
                            );
                        },
                        table: ({ node, className, ...props }) => {
                            const TableWithCopy = () => {
                                const tableRef = useRef<HTMLTableElement>(null);
                                const [copiedTable, setCopiedTable] = useState(false);

                                    const handleCopyTable = () => {
                                        const el = tableRef.current;
                                        if (!el) return;
                                        const text = el.innerText;
                                        navigator.clipboard
                                            .writeText(text)
                                            .then(() => {
                                                setCopiedTable(true);
                                                setTimeout(() => setCopiedTable(false), 1200);
                                            })
                                            .catch(err => console.error("Failed to copy table: ", err));
                                    };

                                    const mergedClassName = [styles.table, className].filter(Boolean).join(" ");

                                    return (
                                        <div className={styles.tableWrapper}>
                                            <button
                                                type="button"
                                                className={styles.copyTableButton}
                                                onClick={handleCopyTable}
                                                aria-label={copiedTable ? "Copied" : "Copy table"}
                                                title={copiedTable ? "Copied" : "Copy table"}
                                            >
                                                {copiedTable ? <ClipboardCheck size={16} /> : <Copy size={16} />}
                                                <span>{copiedTable ? "Copied" : "Copy table"}</span>
                                            </button>
                                            <table {...props} className={mergedClassName} ref={tableRef} />
                                        </div>
                                    );
                                };
                                return <TableWithCopy />;
                            },
                            tr: ({ node, ...props }) => {
                                const hasContent = React.Children.toArray(props.children).some((child: any) => {
                                    if (typeof child === 'string') return child.trim().length > 0;
                                    if (React.isValidElement(child)) {
                                        // Check if the cell has content
                                        const cellChildren = (child as React.ReactElement).props.children;
                                        if (Array.isArray(cellChildren)) {
                                            return cellChildren.some(c => typeof c === 'string' ? c.trim().length > 0 : true);
                                        }
                                        if (typeof cellChildren === 'string') return cellChildren.trim().length > 0;
                                        return !!cellChildren;
                                    }
                                    return false;
                                });
                                
                                if (!hasContent) return null;
                                return <tr {...props} />;
                            },
                            hr: ({ node, ...props }) => <hr {...props} className={styles.hrLine} />
                        }}
                    />
                </div>
            </Stack.Item>

            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>{t("followupQuestions")}</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            <Stack.Item>
                <div className={styles.answerFooter}>
                    {!!parsedAnswer.citations.length && (
                        <div className={styles.citationList}>
                            <span className={styles.citationLearnMore}>{t("citationWithColon")}</span>
                            {parsedAnswer.citations.map((x, i) => {
                                const path = getCitationFilePath(x);
                                const label = getCitationLabel(x);
                                return (
                                    <button key={i} type="button" className={styles.citation} title={label} onClick={() => onCitationClicked(path)}>
                                        {`${i + 1}. ${label}`}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <div className={styles.answerActions}>
                        <button
                            className={styles.answerActionButton}
                            title={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            aria-label={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            onClick={handleCopy}
                        >
                            {copied ? <ClipboardCheck size={20} /> : <Copy size={20} />}
                        </button>

                        {showAdvancedSettings && (
                            <button
                                className={styles.answerActionButton}
                                title={t("tooltips.showThoughtProcess")}
                                aria-label={t("tooltips.showThoughtProcess")}
                                onClick={() => onThoughtProcessClicked()}
                                disabled={!answer.context.thoughts?.length || isStreaming}
                            >
                                <Lightbulb size={20} />
                            </button>
                        )}

                        {showAdvancedSettings && (
                            <button
                                className={styles.answerActionButton}
                                title={t("tooltips.showSupportingContent")}
                                aria-label={t("tooltips.showSupportingContent")}
                                onClick={() => onSupportingContentClicked()}
                                disabled={!answer.context.data_points || isStreaming}
                            >
                                <ClipboardList size={20} />
                            </button>
                        )}
                    </div>
                </div>
            </Stack.Item>
        </Stack>
    );
};
